import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import HomepageFetchHelper from '../helpers/homepage-fetcher.js';
import { extractUrlParams } from '../helpers/pagination.js';
import { removePrecedingSlash } from '../helpers/links.js';
import {
  generateHomepagePreviewContent,
  generateMediaPreviewContent,
  generateGalleryPreviewContent,
} from '@apptegy/preview-content-setter';
import { convertLegacyPageConfigToPageBuilderConfig } from '@cms/page-builder-adapters';
import { useAlertBannerStore } from './alertBannerStore.js';

export const IS_DEV = process.env.NODE_ENV === 'development';
const IS_INTRANET = process.env.NUXT_PUBLIC_IS_INTRANET === 'true';

export const useMainStore = defineStore('main', () => {
  // --- State ---
  const host = ref(null);
  const alias = ref(null);
  const org = ref(null);
  const secondaryOrgId = ref(null);
  const parentOrgId = ref(null);
  const links = ref({});
  const customSectionPaths = ref({});
  const organizations = ref([]);
  const globals = ref({
    favicon: { url: '' },
    info: {
      school_name: '',
      primary_color: { hex: '#000000' },
    },
    contact_form_configuration: {
      topics: [],
      default_email: '',
      published: false,
    },
  });
  const domain = ref('');
  const theme = ref({ containers: [] });
  const accentStyling = ref(false);
  const accentType = ref('dot');
  const font = ref('');
  const homepage = ref({});
  const mediaPreviewContent = ref({});
  const aboutContent = ref({});
  const statsContent = ref({});
  const settings = ref({});
  const menu = ref([]);
  const customMenuItems = ref([]);
  const menuStatus = ref(false);
  const page = ref({});
  const pathPrefix = ref('');
  const configLoading = ref(true);
  const gallery = ref([]);
  const customSections = ref([]);
  const iosLink = ref('');
  const androidLink = ref('');
  const isThemePreviewState = ref(false);
  const needsRefresh = ref(false);
  const pageContentNeedsRefresh = ref(false);
  const sectionId = ref('');
  const sectionName = ref('');
  const eventsHasSections = ref(null);
  const diningDisclaimer = ref({ fda_statement: null });
  const featureFlags = ref({});
  const staticFilesConfig = ref({});
  const authLoggedIn = ref(false);
  const fullSiteMap = ref(null);
  const mediaSubdomain = ref('');
  const navigationSubdomain = ref('');
  const inPageSharedContent = ref([]);
  const clientId = ref(null);

  // --- Getters ---
  const getAlias = computed(() => alias.value);
  const getSecondaryOrgId = computed(() => secondaryOrgId.value);
  const getGlobals = computed(() => globals.value);
  const getMenu = computed(() => menu.value);
  const getTheme = computed(() => theme.value);
  const getGlobalThemeSettings = computed(() => ({
    font: font.value,
    accentStyling: accentStyling.value,
    accentType: accentType.value,
    primaryColor: get(globals.value, 'info.primary_color.hex', '#fff'),
    secondaryColor: get(globals.value, 'info.secondary_color.hex', '#000'),
    linkColor: get(globals.value, 'info.links_color.hex', '#0070E0'),
  }));
  const themeExists = computed(() => !isEmpty(theme.value));
  const getCustomMenuItems = computed(() => customMenuItems.value);
  const getCustomSections = computed(() => customSections.value);
  const getLinks = computed(() => links.value);
  const getLiveFeed = computed(() => {
    const translationLocale = _getTranslationLocale();
    return `${links.value.live_feed}&locale=${translationLocale}`;
  });
  const getOrganizations = computed(() => organizations.value);
  const getHomepage = computed(() => homepage.value);
  const getAndroidLink = computed(() => androidLink.value);
  const getIOSLink = computed(() => iosLink.value);
  const isThemePreview = computed(() => isThemePreviewState.value);
  const getHost = computed(() => host.value);
  const getThemeHeadingColor = computed(() => get(globals.value, 'info.cms_heading_color.hex', null));
  const clientWorkState = computed(() => {
    const translationLocale = _getTranslationLocale();
    const {
      logo,
      watermark,
      favicon,
      secondary_organization_name,
      fallback_images,
      chat_configuration,
      contact_form_configuration,
    } = globals.value;
    const { newsFallbackImage, staffFallbackImage, liveFeedFallbackImage } = settings.value;
    const { id, parent_id, name, slug, in_menu, status, breadcrumbs } = page.value;
    const liveFeedDefaultFallbackImage = fallback_images?.find((item) => item.feature === 'live_feed');
    const newsDefaultFallbackImage = fallback_images?.find((item) => item.feature === 'news');
    const staffDefaultFallbackImage = fallback_images?.find((item) => item.feature === 'staff');

    return {
      host: host.value,
      alias: alias.value || organizations.value.find((o) => o.primary)?.alias,
      secondaryOrgId: secondaryOrgId.value,
      parentOrgId: parentOrgId.value,
      globals: {
        logo,
        watermark,
        favicon,
        secondary_organization_name,
        liveFeedDefaultFallbackImage,
        newsDefaultFallbackImage,
        staffDefaultFallbackImage,
        chat_configuration,
        contact_form_configuration,
      },
      links: links.value,
      settings: {
        newsFallbackImage,
        staffFallbackImage,
        liveFeedFallbackImage,
      },
      organizations: organizations.value,
      translation: { locale: translationLocale },
      page: { id, parent_id, name, slug, in_menu, status, breadcrumbs },
      pathPrefix: pathPrefix.value,
      iosLink: iosLink.value,
      androidLink: androidLink.value,
    };
  });
  const getFullSiteMap = computed(() => fullSiteMap.value);
  const getNavigationSubdomain = computed(() => navigationSubdomain.value || mediaSubdomain.value);

  // Helper to access translation locale without circular import at module level.
  // Lazy import is safe here because pinia ensures store instances exist at call time.
  function _getTranslationLocale() {
    try {
      const { useTranslationStore } = require('./translationStore.js');
      return useTranslationStore().locale;
    } catch {
      return 'en';
    }
  }

  // Getter functions (parameterised)
  function getLiveFeedApi(baseURL) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(customSectionPaths.value.live_feed)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale, max_images: 10 } };
  }

  function getLiveFeedPost(baseURL) {
    if (!customSectionPaths.value?.v4?.live_feed?.main && !customSectionPaths.value.live_feed) return;
    const locale = _getTranslationLocale();
    const liveFeedPath = customSectionPaths.value.v4.live_feed.main || customSectionPaths.value.live_feed;
    const fullUrl = `${baseURL}${removePrecedingSlash(liveFeedPath)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale, max_images: 10 } };
  }

  function getNews(baseURL) {
    const locale = _getTranslationLocale();
    if (!baseURL) return `${links.value.news}&locale=${locale}&not_reset_video_dimensions=true`;
    const fullUrl = `${baseURL}${removePrecedingSlash(customSectionPaths.value.news)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale, not_reset_video_dimensions: 'true' } };
  }

  function getEventsApi(baseURL) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(
      customSectionPaths.value?.v4?.events?.main || customSectionPaths.value.events,
    )}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale } };
  }

  function getEventsSectionApi(baseURL, slug) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(
      `${customSectionPaths.value?.v4?.events?.sections[slug]}` || customSectionPaths.value?.v4?.events?.main,
    )}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale } };
  }

  function getAthletics(baseURL) {
    const locale = _getTranslationLocale();
    if (!baseURL) return `${links.value.athletics}&locale=${locale}`;
    const fullUrl = `${baseURL}${removePrecedingSlash(
      customSectionPaths.value?.v4?.athletics?.main || customSectionPaths.value.athletics,
    )}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale, upcoming: true } };
  }

  function getStaffApi(baseURL) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(
      customSectionPaths.value?.v4?.staff?.main || customSectionPaths.value.staff,
    )}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale } };
  }

  function getDiningApi(baseURL) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(customSectionPaths.value.dining)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale } };
  }

  function getCustomSectionApi(baseURL, path) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(path)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale } };
  }

  function getDocumentsApi(baseURL) {
    const locale = _getTranslationLocale();
    const fullUrl = `${baseURL}${removePrecedingSlash(customSectionPaths.value.documents)}`;
    const { url, params } = extractUrlParams(fullUrl);
    return { url, params: { ...params, locale, not_reset_video_dimensions: 'true' } };
  }

  function getS3UrlForMedia(mediaType) {
    const locale = _getTranslationLocale() || 'en';
    return `/content/${parentOrgId.value}/${secondaryOrgId.value}/${locale}/homepage_${mediaType}.json`;
  }

  // --- Mutations (now plain functions) ---
  function setConfigLoading(payload) {
    configLoading.value = payload;
  }
  function setGallery(payload) {
    gallery.value = payload;
  }

  function setSecondaryOrgId(payload) {
    org.value = payload;
    secondaryOrgId.value = payload;
  }

  function setParentOrgId(payload) {
    parentOrgId.value = payload;
  }
  function setLinks(payload) {
    links.value = payload;
  }
  function setCustomSectionPaths(payload) {
    customSectionPaths.value = payload;
  }
  function setOrganizations(payload) {
    organizations.value = payload;
  }

  function removePathPrefixesFromOrgs() {
    organizations.value = organizations.value.map((o) => {
      o.path_prefix = '';
      return o;
    });
  }

  function setPathPrefix(payload) {
    pathPrefix.value = payload;
  }
  function setGlobals(payload) {
    globals.value = payload;
  }
  function setDomain(payload) {
    domain.value = payload;
  }
  function setTheme(payload) {
    theme.value = payload;
  }
  function setNeedsRefresh(payload) {
    needsRefresh.value = payload;
  }
  function setPageContentNeedsRefresh(payload) {
    pageContentNeedsRefresh.value = payload;
  }
  function setAccentStyling(payload) {
    accentStyling.value = payload;
  }
  function setAccentType(payload) {
    accentType.value = payload;
  }
  function setHomepage(payload) {
    homepage.value = payload;
  }
  function setMediaPreviewContent(payload) {
    mediaPreviewContent.value = payload;
  }
  function setAboutPreviewContent(payload) {
    aboutContent.value = payload;
  }
  function setStatsPreviewContent(payload) {
    statsContent.value = payload;
  }
  function setSettings(payload) {
    settings.value = payload;
  }

  function setFont(payload) {
    font.value = payload === 'Univers Next' || payload === 'Work Sans' ? 'Work Sans' : 'Inter';
  }

  function setMenu(payload) {
    menu.value = payload;
  }
  function setCustomMenuItems(payload) {
    customMenuItems.value = payload;
  }
  function setCustomSections(payload) {
    customSections.value = payload;
  }
  function toggleMenu(status) {
    menuStatus.value = status;
  }
  function closeMenu() {
    menuStatus.value = false;
  }
  function setPageConfig(payload) {
    page.value = payload;
  }
  function setPageSharedContent(payload) {
    inPageSharedContent.value = payload;
  }
  function setIOSLink(payload) {
    iosLink.value = payload;
  }
  function setAndroidLink(payload) {
    androidLink.value = payload;
  }
  function setIsThemePreview(payload) {
    isThemePreviewState.value = payload;
  }

  function setUpPreviewFooter(footerOptions) {
    homepage.value.footer.logo.image_alignment = 'left';
    homepage.value.footer.columns[2].header = 'Quick Links';
    homepage.value.footer.logo.url = globals.value.logo.url || '';
    homepage.value.footer.organization_info = {
      zip: '71901',
      city: 'Hot Springs',
      name: globals.value.info.school_name || '',
      state: 'AR',
      address: '200 Whittington Avenue',
    };
    if (footerOptions.version === 'F2') {
      homepage.value.footer.logo.image_alignment = 'center';
      homepage.value.footer.columns[2].header = 'Stay Connected';
    }
  }

  function setUpPreviewGallery(galleryOptions) {
    const galleryInfoByOrg = generateGalleryPreviewContent(globals.value.info.organization_type);
    homepage.value.gallery =
      galleryOptions.use_video && ['G2', 'G7'].includes(galleryOptions.version)
        ? galleryInfoByOrg.videoContent
        : galleryInfoByOrg.staticImageContent;
  }

  function setSectionId(id) {
    sectionId.value = id;
  }
  function setClientId(id) {
    clientId.value = id;
  }
  function setSectionName(name) {
    sectionName.value = name;
  }
  function setEventsHasSections(payload) {
    eventsHasSections.value = payload;
  }
  function setHost(value) {
    host.value = value;
  }
  function setAlias(payload) {
    alias.value = payload;
  }
  function setDiningDisclaimer(disclaimer) {
    diningDisclaimer.value = disclaimer;
  }
  function setFeatureFlags(payload) {
    featureFlags.value = payload;
  }
  function setStaticFilesConfig(payload) {
    staticFilesConfig.value = payload;
  }
  function setAuthUserLoaded(payload) {
    authLoggedIn.value = payload;
  }
  function setFullSiteMap(payload) {
    fullSiteMap.value = payload;
  }
  function setMediaSubdomain(payload) {
    mediaSubdomain.value = payload;
  }
  function setNavigationSubdomain(payload) {
    navigationSubdomain.value = payload;
  }

  // --- Private helpers used by nuxtServerInit ---
  function _setConfig(translationStore, configData) {
    setTheme(configData.active_theme.content);
    setAccentStyling(configData.active_theme.accent_styling);
    setAccentType(configData.active_theme.accent_type);
    setFont(configData.active_theme.font);
    setHomepage(configData.homepage);
    setSettings(configData.settings);
    setGlobals(configData);
    translationStore.setComponentTranslations(configData.component_translations);
  }

  function _setDataForThemePreview(translationStore, siteStructureData) {
    setIsThemePreview(true);
    setIOSLink('https://www.apple.com/ios/app-store');
    setAndroidLink('https://play.google.com/store');
    removePathPrefixesFromOrgs();

    const { config: configData, alert_banner: alertBanner, alert_banners: alertBannerSeparateData } = siteStructureData;
    setGlobals(configData);
    translationStore.setComponentTranslations(configData.component_translations);
    setAccentType(configData.active_theme.accent_type);
    setSettings({
      ...configData.settings,
      liveFeedFallbackImage: {
        url:
          configData.info.organization_type === 'Municipality'
            ? 'https://d2kzwze52p8717.cloudfront.net/city-streets.jpg'
            : 'https://d2kzwze52p8717.cloudfront.net/gallery_01.png',
        alt: 'live feed default image',
      },
    });

    const homepagePreviewContent = generateHomepagePreviewContent(configData.info.organization_type);
    setAboutPreviewContent(homepagePreviewContent.aboutPreviewContent);
    setStatsPreviewContent(homepagePreviewContent.statsPreviewContent);
    setMenu(homepagePreviewContent.siteMenuDefaults);
    setHomepage({
      ...homepagePreviewContent.editableHomepageComponents,
      header: {
        links: homepagePreviewContent.utilityLinkDefaults,
        icon_links: homepagePreviewContent.editableHomepageComponents.header.icon_links,
      },
      schools_menu: homepagePreviewContent.orgMenuDefaults,
    });
    setOrganizations(homepagePreviewContent.orgDefaults);

    const mediaPreview = generateMediaPreviewContent(configData.info.organization_type);
    setMediaPreviewContent(mediaPreview);

    const alertBannerStore = useAlertBannerStore();
    alertBannerStore.setAlertBannerConfig({
      ...alertBanner,
      content: homepagePreviewContent.alertDefaults.data,
    });
    alertBannerStore.setAlertBannerSeparate({
      data: [...alertBannerSeparateData, ...homepagePreviewContent.alertSeparateDefaults],
      preview: true,
    });

    setConfigLoading(false);
  }

  async function _setDomainData(translationStore, domainData) {
    setPathPrefix(domainData.path_prefix);
    setLinks(domainData.links);
    setCustomSectionPaths(domainData.paths);
    setIOSLink(domainData.ios_app_link);
    setAndroidLink(domainData.android_app_link);
    setOrganizations(domainData.organizations);
    translationStore.setLanguages(domainData.languages);
    setMediaSubdomain(domainData.media_subdomain);
    setNavigationSubdomain(domainData.navigation_subdomain);
    setClientId(domainData.client_id);
    const { useCmsAiStore } = await import('./cmsAiStore.js');
    const cmsAiStore = useCmsAiStore();
    cmsAiStore.setClientId(domainData.client_id);
    if (domainData.cms_ai_settings) {
      cmsAiStore.setCmsAiSettings(domainData.cms_ai_settings);
    }
  }

  function _setTranslationData(translationStore, cookies) {
    translationStore.setLanguage(cookies.langCode);
    translationStore.setLanguageText(cookies.language);
  }

  function _setRequestData(translationStore, requestData) {
    setAlias(requestData.alias);
    setHost(requestData.host);
    setDomain(requestData.domain);
    if (requestData.locale) {
      _setTranslationData(translationStore, requestData.cookies);
    }
  }

  // --- Actions ---
  async function nuxtServerInit(context) {
    const { useTranslationStore } = await import('./translationStore.js');
    const translationStore = useTranslationStore();

    const nuxtServerInitHelper = new HomepageFetchHelper(context.event);

    const requestData = nuxtServerInitHelper.extractDataFromInitialRequest(IS_DEV);
    await _setRequestData(translationStore, requestData);

    let orgDetails;
    let siteMetadata;
    let siteStructureData;

    if (IS_INTRANET && !context.authCookie) {
      return;
    }

    try {
      try {
        orgDetails = await nuxtServerInitHelper.fetchOrgDetails(requestData, alias.value || '');
        siteMetadata = await nuxtServerInitHelper.fetchSiteMetadata(orgDetails);
        siteStructureData = await nuxtServerInitHelper.fetchSiteStructure(
          requestData,
          orgDetails,
          siteMetadata,
          IS_INTRANET,
          context.authCookie,
        );
        setStaticFilesConfig(siteMetadata.files_config);
        setFeatureFlags(siteMetadata.feature_flags);
      } catch {
        console.warn('error fetching site structure - falling back to legacy APIs');
        try {
          console.error('Static files not present, falling back to legacy APIs');
          const legacyData = await nuxtServerInitHelper.fetchLegacyAPIs(requestData);
          orgDetails = legacyData.orgDetails;
          siteStructureData = legacyData.siteStructureData;
          setFeatureFlags(legacyData.featureFlagsData);
        } catch (error) {
          const message =
            error.statusCode === 404
              ? `(Site Structure not found)${error.message}`
              : error.message || 'Unexpected server error';
          throw createError({ message, statusCode: error.statusCode || 500 });
        }
      }

      setParentOrgId(orgDetails.parent_organization_id);
      setSecondaryOrgId(orgDetails.secondary_organization_id);
    } catch (error) {
      throw createError({
        message: error.message || 'Unexpected server error',
        statusCode: error.statusCode || 500,
      });
    }

    const pageHasRedirect = await nuxtServerInitHelper.handleRedirects(siteStructureData.redirects, context.nuxt);
    if (pageHasRedirect) return;

    const newsRedirectPath = HomepageFetchHelper.getArticleRedirectUrl(context.event.req);
    if (newsRedirectPath) {
      context.nuxt.runWithContext(async () => {
        await navigateTo(newsRedirectPath, { external: true });
      });
      return;
    }

    setCustomMenuItems(siteStructureData.custom_menu_items);
    await _setDomainData(translationStore, siteStructureData.domain);

    if (nuxtServerInitHelper.isThemePreview) {
      try {
        const customMenuItemsData = await nuxtServerInitHelper.fetchCustomMenuItems(secondaryOrgId.value);
        setCustomMenuItems(customMenuItemsData);
      } catch (e) {
        console.error(e);
      }
      await _setDataForThemePreview(translationStore, siteStructureData);
      return;
    }

    setMenu(siteStructureData.sitemap);
    const alertBannerStore = useAlertBannerStore();
    alertBannerStore.setAlertBannerConfig(siteStructureData.alert_banner);
    alertBannerStore.setAlertBannerSeparate(siteStructureData.alert_banners);
    setCustomSections(siteStructureData.custom_sections);

    await _setConfig(translationStore, siteStructureData.config);
    setConfigLoading(false);
  }

  async function setPageData(payload) {
    if (menuStatus.value) toggleMenu(false);
    const pageRendererEnabled =
      !!featureFlags.value.cms_page_renderer_package_enabled && !!payload?.flags?.new_renderer_enabled;
    let pageConfig = payload;

    if (pageRendererEnabled) {
      const convertedContent = convertLegacyPageConfigToPageBuilderConfig(payload.content);
      pageConfig = { ...payload, content: convertedContent };
      setPageSharedContent(payload.shared_layouts || []);
    }
    setPageConfig(pageConfig);
  }

  function setCustomSectionData({ type, fallbackSectionName }) {
    const section = customSections.value.find((s) => s.type === type);
    const name = section ? section.name : fallbackSectionName;
    setSectionId(section ? section.id : -1);
    setSectionName(name);
  }

  return {
    // State (exposed for direct access and Pinia devtools)
    host,
    alias,
    org,
    secondaryOrgId,
    parentOrgId,
    links,
    customSectionPaths,
    organizations,
    globals,
    domain,
    theme,
    accentStyling,
    accentType,
    font,
    homepage,
    mediaPreviewContent,
    aboutContent,
    statsContent,
    settings,
    menu,
    customMenuItems,
    menuStatus,
    page,
    pathPrefix,
    configLoading,
    gallery,
    customSections,
    iosLink,
    androidLink,
    isThemePreviewState,
    needsRefresh,
    pageContentNeedsRefresh,
    sectionId,
    sectionName,
    eventsHasSections,
    diningDisclaimer,
    featureFlags,
    staticFilesConfig,
    authLoggedIn,
    fullSiteMap,
    mediaSubdomain,
    navigationSubdomain,
    inPageSharedContent,
    clientId,
    // Getters
    getAlias,
    getSecondaryOrgId,
    getGlobals,
    getMenu,
    getTheme,
    getGlobalThemeSettings,
    themeExists,
    getCustomMenuItems,
    getCustomSections,
    getLinks,
    getLiveFeed,
    getOrganizations,
    getHomepage,
    getAndroidLink,
    getIOSLink,
    isThemePreview,
    getHost,
    getThemeHeadingColor,
    clientWorkState,
    getFullSiteMap,
    getNavigationSubdomain,
    // Parameterised getter functions
    getLiveFeedApi,
    getLiveFeedPost,
    getNews,
    getEventsApi,
    getEventsSectionApi,
    getAthletics,
    getStaffApi,
    getDiningApi,
    getCustomSectionApi,
    getDocumentsApi,
    getS3UrlForMedia,
    // Setters / mutations
    setConfigLoading,
    setGallery,
    setSecondaryOrgId,
    setParentOrgId,
    setLinks,
    setCustomSectionPaths,
    setOrganizations,
    removePathPrefixesFromOrgs,
    setPathPrefix,
    setGlobals,
    setDomain,
    setTheme,
    setNeedsRefresh,
    setPageContentNeedsRefresh,
    setAccentStyling,
    setAccentType,
    setHomepage,
    setMediaPreviewContent,
    setAboutPreviewContent,
    setStatsPreviewContent,
    setSettings,
    setFont,
    setMenu,
    setCustomMenuItems,
    setCustomSections,
    toggleMenu,
    closeMenu,
    setPageConfig,
    setPageSharedContent,
    setIOSLink,
    setAndroidLink,
    setIsThemePreview,
    setUpPreviewFooter,
    setUpPreviewGallery,
    setSectionId,
    setClientId,
    setSectionName,
    setEventsHasSections,
    setHost,
    setAlias,
    setDiningDisclaimer,
    setFeatureFlags,
    setStaticFilesConfig,
    setAuthUserLoaded,
    setFullSiteMap,
    setMediaSubdomain,
    setNavigationSubdomain,
    // Actions
    nuxtServerInit,
    setPageData,
    setCustomSectionData,
  };
});
